// =============================================================================
// metro_cache_v2.js — Carpe IndexedDB cache for homebase metro
// =============================================================================
// Layered ON TOP of the CDN. Order of priority on app launch:
//   1. IDB cache (homebase only)         — instant, offline-capable
//   2. CDN blob (cdn.carpe-app.com)      — ~50ms, edge-cached globally
//   3. Supabase REST                     — fallback for uncovered metros
//
// Storage shape:
//   key:           "<metro_slug>:<dataType>"   e.g. "brooklyn:restaurants"
//   record:        { key, data: [...], cachedAt, schemaVersion }
//
// Fixes vs v1 of this module:
//   - Singleton DB promise no longer traps null forever — retries on next call
//   - Every record carries schemaVersion; reads with mismatched version return null
//   - _normalizeMetro produces stable lowercase hyphenated slug, not raw city
//   - LRU sweep on every successful write — keeps top N most-recently-used metros
//   - Quota errors trigger a partial purge instead of dying
// =============================================================================

const METRO_CACHE_DB = "carpe_metro_cache";
const METRO_CACHE_STORE = "metros";
const METRO_CACHE_VERSION = 2; // bumped — clears v1 records on first run
const SCHEMA_VERSION = 1; // matches CDN blob shape; bump in lockstep

const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h fresh
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7d hard expiry
const MAX_METROS_RETAINED = 8; // LRU cap — beyond this, oldest evicted

let _dbPromise = null;
let _dbFailedOnce = false; // tracks last attempt — failures aren't permanent

// ── DB open with retry-on-null ──────────────────────────────────────────────
const _openDB = () => {
  // If the previous attempt failed, _dbPromise resolved to null. Allow retry.
  if (_dbPromise && !_dbFailedOnce) return _dbPromise;
  _dbFailedOnce = false;
  _dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === "undefined") {
      _dbFailedOnce = true;
      return resolve(null);
    }
    try {
      const req = indexedDB.open(METRO_CACHE_DB, METRO_CACHE_VERSION);
      req.onerror = () => {
        _dbFailedOnce = true;
        resolve(null);
      };
      req.onsuccess = () => resolve(req.result);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        // Wipe v1 store and recreate clean — schema shape differs.
        if (db.objectStoreNames.contains(METRO_CACHE_STORE)) {
          db.deleteObjectStore(METRO_CACHE_STORE);
        }
        const store = db.createObjectStore(METRO_CACHE_STORE, { keyPath: "key" });
        store.createIndex("cachedAt", "cachedAt", { unique: false });
      };
    } catch {
      _dbFailedOnce = true;
      resolve(null);
    }
  });
  return _dbPromise;
};

// ── Slug normalization — must match the generator's slugify() exactly ──────
export const metroSlug = (input) => {
  if (!input) return "";
  return String(input)
    .split(",")[0]
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

// ── Read ────────────────────────────────────────────────────────────────────
export const metroCacheGet = async (metroName, dataType) => {
  const slug = metroSlug(metroName);
  if (!slug) return null;
  const db = await _openDB();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(METRO_CACHE_STORE, "readonly");
      const req = tx.objectStore(METRO_CACHE_STORE).get(`${slug}:${dataType}`);
      req.onsuccess = () => {
        const r = req.result;
        if (!r) return resolve(null);
        // Version mismatch — pretend it doesn't exist; will be overwritten on next write.
        if (r.schemaVersion !== SCHEMA_VERSION) return resolve(null);
        if (Date.now() - r.cachedAt > CACHE_MAX_AGE_MS) return resolve(null);
        resolve({ data: r.data, cachedAt: r.cachedAt });
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
};

// ── Write ───────────────────────────────────────────────────────────────────
export const metroCacheSet = async (metroName, dataType, data) => {
  const slug = metroSlug(metroName);
  if (!slug || !Array.isArray(data) || data.length === 0) return false;
  const db = await _openDB();
  if (!db) return false;

  const writeRecord = () =>
    new Promise((resolve) => {
      try {
        const tx = db.transaction(METRO_CACHE_STORE, "readwrite");
        tx.objectStore(METRO_CACHE_STORE).put({
          key: `${slug}:${dataType}`,
          data,
          cachedAt: Date.now(),
          schemaVersion: SCHEMA_VERSION,
        });
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
        tx.onabort = () => resolve(false);
      } catch (err) {
        // QuotaExceededError lands here — trigger LRU sweep then retry once.
        if (err?.name === "QuotaExceededError") {
          _evictLRU(db, MAX_METROS_RETAINED / 2).then(() => resolve(false));
        } else {
          resolve(false);
        }
      }
    });

  const ok = await writeRecord();
  if (ok) {
    // Fire-and-forget LRU sweep on success — caps disk use over time.
    _evictLRU(db, MAX_METROS_RETAINED).catch(() => {});
  }
  return ok;
};

// ── LRU eviction — keeps the N most-recently-cached metros ─────────────────
async function _evictLRU(db, keepCount) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(METRO_CACHE_STORE, "readwrite");
      const store = tx.objectStore(METRO_CACHE_STORE);
      const req = store.getAll();
      req.onsuccess = () => {
        const all = req.result || [];
        // Group records by slug, find oldest cachedAt per slug.
        const bySlug = new Map();
        for (const rec of all) {
          const slug = rec.key.split(":")[0];
          const cur = bySlug.get(slug);
          if (!cur || rec.cachedAt < cur) bySlug.set(slug, rec.cachedAt);
        }
        if (bySlug.size <= keepCount) return resolve(true);

        // Sort slugs by oldest cachedAt, drop everything beyond keepCount.
        const sorted = [...bySlug.entries()].sort((a, b) => a[1] - b[1]);
        const toEvict = new Set(
          sorted.slice(0, sorted.length - keepCount).map(([s]) => s)
        );
        for (const rec of all) {
          const slug = rec.key.split(":")[0];
          if (toEvict.has(slug)) store.delete(rec.key);
        }
        tx.oncomplete = () => resolve(true);
        tx.onerror = () => resolve(false);
      };
      req.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export const metroCacheIsFresh = (cachedAt) =>
  Date.now() - cachedAt < CACHE_TTL_MS;

export const metroCacheClear = async () => {
  const db = await _openDB();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(METRO_CACHE_STORE, "readwrite");
      tx.objectStore(METRO_CACHE_STORE).clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
};

// Expose for tests/debugging
export const __internal = {
  SCHEMA_VERSION,
  MAX_METROS_RETAINED,
  CACHE_TTL_MS,
  CACHE_MAX_AGE_MS,
  metroSlug,
};
